import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { geocodingServices, houseToHouseServices, hthHelper, mapServices } from 'src/services';
import { getTerritoryColor } from './mapConstants';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete, InfoWindow } from '@react-google-maps/api';
import { googleMapConfig, isLocalhost } from 'src/app-config';
import { H2, Loading } from 'src/components/_commons';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { typeCoords, typeHTHTerritory, typeHTHFace, typeMapFamilia, typeMapToast } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { useGetConfig, useGetIsDarkMode, useGetIsMobile, useTerritoryDates } from 'src/custom-hooks';
import { useNavigate } from 'react-router';
import BlockPolygon from './BlockPolygon';
import React from 'react';

// Función para limpiar el formulario de familia
const initialForm = { familia: '', direccion: '', diasHorarios: '', activo: true };

export const AdminsMapa = () => {
    const [address, setAddress] = useState("");
    const [centerCoords, setCenterCoords] = useState<typeCoords | null>(null);
    const [hthTerritories, setHTHTerritories] = useState<typeHTHTerritory[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGeolocationReady, setIsGeolocationReady] = useState(false);
    const config = useGetConfig();
    const dispatch = useDispatch();
    const isMobile = useGetIsMobile();
    const isDarkMode = useGetIsDarkMode();
    const map = useRef<any>(null);
    const mapLoader = useJsApiLoader(googleMapConfig);
    const navigate = useNavigate();
    const [familias, setFamilias] = useState<typeMapFamilia[]>([]);
    const [form, setForm] = useState(initialForm);
    const [adding, setAdding] = useState(false);
    const [geocodingLoading, setGeocodingLoading] = useState(false);
    const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
    const [selectedFamilia, setSelectedFamilia] = useState<typeMapFamilia | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [showNumbers, setShowNumbers] = useState(true);
    const [showFamilies, setShowFamilies] = useState(true);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedTerritories, setSelectedTerritories] = useState<string[]>([]);
    const [savedSelections, setSavedSelections] = useState<{name: string, territories: string[]}[]>([]);
    const [selectedSelection, setSelectedSelection] = useState<string>('');
    const [toast, setToast] = useState<typeMapToast|null>(null);
    const familiaInputRef = useRef<HTMLInputElement>(null);
    const { territoryDates, loading: loadingDates, error: errorDates, handleFile } = useTerritoryDates();
    const [showDates, setShowDates] = useState(false);
    const [loadingMapData, setLoadingMapData] = useState(true);
    const [loadingFamilia, setLoadingFamilia] = useState(false);
    const [loadingSelection, setLoadingSelection] = useState(false);

    // Centraliza limpieza de formulario
    const resetForm = () => {
        setForm(initialForm);
        setEditId(null);
        setAdding(false);
    };
    // Centraliza limpieza de selección
    const clearSelection = () => {
        setSelectedTerritories([]);
        setSelectedSelection('');
    };

    const openModalHandler = (territoryNumber: string) => {
        dispatch(setValuesAndOpenAlertModalReducer({
            mode: 'confirm',
            title: "Confirmar",
            message: `¿Ir al territorio ${territoryNumber}?`,
            execution: () => {
                navigate(`/casa-en-casa/${territoryNumber}`);
            }
        }));
    }

    useEffect(() => {
        if (!navigator.geolocation) {
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'alert',
                title: "Error",
                message: "La aplicación no tiene permisos del navegador para usar la ubicación"
            }));
            setIsLoading(false);
            return;
        }
        
        navigator.geolocation.getCurrentPosition((geoPosition: GeolocationPosition) => {
            if (!geoPosition) {
                dispatch(setValuesAndOpenAlertModalReducer({
                    mode: 'alert',
                    title: "Error",
                    message: "Algo falló al geolocalizar"
                }));
                setIsLoading(false);
                setIsGeolocationReady(true);
                return;
            }
            
            const position: typeCoords = {
                lat: isLocalhost ? config.centralPointCoordinates?.lat ?? -34.632126 : geoPosition.coords.latitude,
                lng: isLocalhost ? config.centralPointCoordinates?.lng ?? -58.456176 : geoPosition.coords.longitude
            };
            setCenterCoords(position);
            setIsGeolocationReady(true);
            geocodingServices.GetAddressFromCoordinates(position).then(response => {
                if (!response.success || !response.data?.address) {
                    setAddress("-,");
                } else {
                    setAddress(response.data.address);
                }
            });
        }, (error) => {
            console.error('Error de geolocalización:', error);
            dispatch(setValuesAndOpenAlertModalReducer({
                mode: 'alert',
                title: "Error de ubicación",
                message: "No se pudo obtener tu ubicación. Verifica los permisos del navegador."
            }));
            setIsLoading(false);
        });
    }, [dispatch]);

    useEffect(() => {
        if (hthTerritories) {
            setIsLoading(false);
            return;
        }
        if (!isGeolocationReady) {
          return;
        }
        houseToHouseServices.GetTerritoriesForMap().then(response => {
          setIsLoading(false);
            if (!response.success || !response.data?.hthTerritories) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setHTHTerritories(response.data.hthTerritories);
        });
    }, [isGeolocationReady]);

    // Cargar datos del mapa desde la API
    useEffect(() => {
        const loadMapData = async () => {
            const response = await mapServices.Get();
            setLoadingMapData(false);
            if (!response.success || !response.data?.mapDoc) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setFamilias(response.data.mapDoc.familias);
            setSavedSelections(response.data.mapDoc.selections);
        }
        if (isGeolocationReady) {
            loadMapData();
        }
    }, [isGeolocationReady]);

    // Toast auto-hide
    useEffect(() => {
        if (toast) {
            const t = setTimeout(() => setToast(null), 2000);
            return () => clearTimeout(t);
        }
    }, [toast]);

    // Focus automático en input Familia
    useEffect(() => {
        if (adding && familiaInputRef.current) {
            familiaInputRef.current.focus();
        }
    }, [adding]);

    // Guardar selección actual
    const handleSaveSelection = async () => {
        let name = prompt('Nombre para la selección:');
        if (!name?.trim()) {
            return;
        }
        name = name.trim();
        const exists = savedSelections.find(sel => sel.name === name);
        if (exists) {
            if (!window.confirm('Ya existe una selección con ese nombre. ¿Deseas sobrescribirla?')) {
                return;
            }
            setLoadingSelection(true);
            const response = await mapServices.UpdateSelection({ name, territories: selectedTerritories });
            setLoadingSelection(false);
            if (response.success) {
                const newSelections = savedSelections.map(sel => sel.name === name ? { name, territories: selectedTerritories } : sel);
                setSavedSelections(newSelections);
                setToast({ msg: 'Selección sobrescrita', type: 'info' });
            } else {
                setToast({ msg: 'Error al guardar selección', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        } else {
            setLoadingSelection(true);
            const response = await mapServices.AddSelection({ name, territories: selectedTerritories });
            setLoadingSelection(false);
            if (response.success) {
                const newSelections = [...savedSelections, { name, territories: selectedTerritories }];
                setSavedSelections(newSelections);
                setToast({ msg: 'Selección guardada', type: 'success' });
            } else {
                setToast({ msg: 'Error al guardar selección', type: 'danger' });
                dispatch(setAlertResponse({ response }));
            }
        }
    }

    // Cargar selección
    const handleLoadSelection = (name: string) => {
        const sel = savedSelections.find(s => s.name === name);
        if (sel) {
            setSelectionMode(true);
            setSelectedTerritories(sel.territories);
            setSelectedSelection(name);
            setToast({ msg: 'Selección cargada', type: 'info' });
        }
    }

    // Eliminar selección
    const handleDeleteSelection = async (name: string) => {
        if (!window.confirm('¿Eliminar la selección guardada?')) {
          return;
        }
        setLoadingSelection(true);
        const response = await mapServices.DeleteSelection(name);
        setLoadingSelection(false);
        if (response.success) {
            const newSelections = savedSelections.filter(s => s.name !== name);
            setSavedSelections(newSelections);
            setSelectedSelection('');
            setToast({ msg: 'Selección eliminada', type: 'danger' });
        } else {
            setToast({ msg: 'Error al eliminar selección', type: 'danger' });
            dispatch(setAlertResponse({ response }));
        }
    }

    // Alta o modificación de familia
    const handleAddFamilia = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoadingFamilia(true);
        setGeocodingLoading(true);
        // Geocodificar dirección
        const response = await geocodingServices.GetCoordinatesFromAddress(form.direccion);
        if (!response.success || !response.data?.coordinates?.lat || !response.data.coordinates.lng) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        const lat = response.data.coordinates.lat;
        const lng = response.data.coordinates.lng;
        setGeocodingLoading(false);
        if (editId) {
          // Modificación
            const familiaToUpdate = familias.find(f => f.id === editId);
            if (familiaToUpdate) {
                const updatedFamilia = { ...familiaToUpdate, ...form, lat, lng };
                const response = await mapServices.UpdateFamilia(updatedFamilia);
                setLoadingFamilia(false);
                if (response.success) {
                    const nuevas = familias.map(f =>
                        f.id === editId ? updatedFamilia : f
                    );
                    setFamilias(nuevas);
                    setEditId(null);
                    setToast({ msg: 'Familia modificada exitosamente', type: 'success' });
                } else {
                    setToast({ msg: 'Error al modificar familia', type: 'danger' });
                    dispatch(setAlertResponse({ response }));
                }
            }
        } else {
            // Alta
            const nuevaFamilia: typeMapFamilia = {
                id: 0,
                familia: form.familia,
                direccion: form.direccion,
                diasHorarios: form.diasHorarios,
                activo: form.activo,
                lat,
                lng
            };
            dispatch(showLoadingModalReducer());
            const response = await mapServices.AddFamilia(nuevaFamilia);
            dispatch(hideLoadingModalReducer());
            setLoadingFamilia(false);
            if (response.success) {
                const nuevas = [...familias, nuevaFamilia];
                setFamilias(nuevas);
                setToast({msg: 'Familia agregada exitosamente', type: 'success'});
            } else {
                setToast({msg: 'Error al agregar familia', type: 'danger'});
                dispatch(setAlertResponse({ response }));
            }
            setForm({ familia: '', direccion: '', diasHorarios: '', activo: true });
            setAdding(false);
        }
    }

    // Baja de familia
    const handleDeleteFamilia = async (id: number) => {
        setLoadingFamilia(true);
        const response = await mapServices.DeleteFamilia(id);
        setLoadingFamilia(false);
        if (response.success) {
            const nuevas = familias.filter(f => f.id !== id);
            setFamilias(nuevas);
            setToast({ msg: 'Familia eliminada exitosamente', type: 'success' });
        } else {
            setToast({ msg: 'Error al eliminar familia', type: 'danger' });
            dispatch(setAlertResponse({ response }));
        }
    }

    // Toggle activo
    const handleToggleActivo = async (id: number) => {
        const familiaToUpdate = familias.find(f => f.id === id);
        if (familiaToUpdate) {
            const updatedFamilia = {
                ...familiaToUpdate,
                activo: !familiaToUpdate.activo
            };
            setLoadingFamilia(true);
            dispatch(showLoadingModalReducer());
            const response = await mapServices.UpdateFamilia(updatedFamilia);
            dispatch(hideLoadingModalReducer());
            setLoadingFamilia(false);
            if (response.success) {
                const nuevas = familias.map(f => f.id === id ? updatedFamilia : f);
                setFamilias(nuevas);
                setToast({msg: 'Estado de familia actualizado', type: 'success'});
            } else {
                setToast({msg: 'Error al actualizar familia', type: 'danger'});
                dispatch(setAlertResponse({ response }));
            }
        }
    }

    const extractInitials = (familia: string) => {
        return familia.split(' ').map(word => word[0]).join('');
    }

    // Handler para selección de territorios
    const handleSelectTerritory = (territoryNumber: string) => {
        if (!selectionMode) {
            return;
        }
        setSelectedTerritories(prev =>
            prev.includes(territoryNumber)
                ? prev.filter(n => n !== territoryNumber)
                : [...prev, territoryNumber]
        );
    }

    // Calculo de estadísticas de selección
    const totalTerritories = hthTerritories ? hthTerritories.length : 0;
    const selectedCount = selectedTerritories.length;
    const percentSelected = totalTerritories > 0 ? Math.round((selectedCount / totalTerritories) * 100) : 0;

    // Mostrar loading mientras se carga la API de Google Maps o mientras se obtienen los datos
    if (!mapLoader.isLoaded || isLoading || loadingMapData) {
        return (
            <div className={'container'}>
                <H2 title={'MAPA DE TERRITORIOS'} />
                <Loading mt={'80px'} />
            </div>
        );
    }

    // Mostrar error si falla la carga de Google Maps
    if (mapLoader.loadError) {
        const errorMessage = mapLoader.loadError.message;
        const isRefererError = errorMessage.includes('RefererNotAllowedMapError');
        
        return (
            <div className={'container'}>
                <H2 title={'MAPA DE TERRITORIOS'} />
                <div className={`alert alert-danger mt-3 ${isDarkMode ? 'text-white' : ''}`}>
                    <h4>Error al cargar el mapa</h4>
                    {isRefererError ? (
                        <>
                            <p><strong>Error de configuración de Google Maps:</strong></p>
                            <p>La API key no está configurada para este dominio.</p>
                            <p>Para desarrollo local, necesitas configurar la API key en la consola de Google Cloud para incluir:</p>
                            <ul>
                                <li><code>http://localhost:3000</code></li>
                                <li><code>http://localhost:3001</code></li>
                                <li><code>http://127.0.0.1:3000</code></li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <p>No se pudo cargar Google Maps. Verifica tu conexión a internet.</p>
                            <p><strong>Error:</strong> {errorMessage}</p>
                        </>
                    )}
                </div>
                
                {/* Mostrar lista de territorios como alternativa */}
                {hthTerritories && hthTerritories.length > 0 && (
                    <div className={`mt-4 ${isDarkMode ? 'text-white' : ''}`}>
                        <div className="row">
                            {hthTerritories.map(territory => (
                                <div key={territory.territoryNumber} className="col-md-6 col-lg-4 mb-3">
                                    <div className={`card ${isDarkMode ? 'bg-dark text-white border-light' : ''}`}>
                                        <div className="card-body">
                                            <h5 className="card-title">Territorio {territory.territoryNumber}</h5>
                                            <p className="card-text">
                                                Manzanas: {territory.faces.length}
                                            </p>
                                            <button 
                                                className="btn btn-primary btn-sm"
                                                onClick={() => navigate(`/casa-en-casa/${territory.territoryNumber}`)}
                                            >
                                                Ver Territorio
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Si no hay coordenadas centrales, mostrar mensaje
    if (!centerCoords) {
        return (
            <div className={'container'}>
                <div className={`alert alert-warning mt-3 ${isDarkMode ? 'text-white' : ''}`}>
                    <h4>Esperando ubicación</h4>
                    <p>Obteniendo tu ubicación actual...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`container ${isDarkMode ? 'text-white' : ''} mt-5`}>
            <H2 title={'MAPA DE TERRITORIOS'} />
            {toast && (
                <div className={`alert alert-${toast.type} position-fixed top-0 start-50 translate-middle-x mt-3 shadow`} style={{zIndex: 9999, minWidth: 250}}>
                    {toast.msg}
                </div>
            )}
            {loadingMapData && (
                <div className="alert alert-info d-flex align-items-center gap-3 py-2 mb-2">
                    <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    <strong>Cargando datos del mapa...</strong>
                </div>
            )}
            {selectionMode && (
                <div className="alert alert-warning d-flex align-items-center gap-3 py-2 mb-2">
                    <strong>Modo selección activado</strong>
                
                </div>
            )}
            {/* Grupo de botones de visualización juntos */}
            <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                <button className={`btn btn-sm ${showNumbers ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setShowNumbers(v => !v)}>
                    {showNumbers ? <BsEyeSlash className="me-1" /> : <BsEye className="me-1" />}
                    {showNumbers ? 'Ocultar números' : 'Mostrar números'}
                </button>
                <button className={`btn btn-sm ${showFamilies ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setShowFamilies(v => !v)}>
                    {showFamilies ? <BsEyeSlash className="me-1" /> : <BsEye className="me-1" />}
                    {showFamilies ? 'Ocultar familias' : 'Mostrar familias'}
                </button>
                <button className={`btn btn-sm ${showDates ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setShowDates(v => !v)} disabled={!territoryDates || Object.keys(territoryDates).length === 0}>
                    {showDates ? <BsEyeSlash className="me-1" /> : <BsEye className="me-1" />}
                    {showDates ? 'Ocultar fechas' : 'Mostrar fechas'}
                </button>
                <button className={`btn btn-sm ${selectionMode ? 'btn-warning' : 'btn-outline-warning'}`} onClick={() => {
                    setSelectionMode(v => !v);
                    clearSelection();
                }}>
                    {selectionMode ? 'Desactivar selección' : 'Activar selección'}
                </button>
            </div>
            {selectionMode && (
                <div className="d-flex gap-2 align-items-center mt-1 flex-wrap">
                    <select
                        className="form-select form-select-sm w-auto"
                        value={selectedSelection}
                        onChange={e => {
                            if (e.target.value === '__none__') {
                                clearSelection();
                                return;
                            }
                            handleLoadSelection(e.target.value);
                        }}
                    >
                        <option value="__none__">Ninguno</option>
                        <option value="">Cargar selección...</option>
                        {savedSelections.map(sel => (
                            <option key={sel.name} value={sel.name}>{sel.name}</option>
                        ))}
                    </select>
                    <button className="btn btn-info btn-sm" disabled={selectedTerritories.length === 0 || loadingSelection} onClick={handleSaveSelection} title="Guarda la selección actual de territorios">
                        {loadingSelection ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Guardando...
                          </>
                        ) : (
                          'Guardar selección'
                        )}
                    </button>
                    {selectedSelection && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSelection(selectedSelection)} disabled={loadingSelection}>
                            {loadingSelection ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Eliminando...
                              </>
                            ) : (
                              'Eliminar selección'
                            )}
                        </button>
                    )}
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => { clearSelection(); }} disabled={loadingSelection}>Limpiar selección</button>
                    {/* Estadísticas de selección */}
                    <span className="badge bg-primary ms-2" style={{fontSize: '1em'}}>
                        {percentSelected}% ({selectedCount}/{totalTerritories})
                    </span>
                </div>
            )}
            <div className={`mt-3 ${isDarkMode ? 'text-dark' : ''}`}>
                <GoogleMap
                    id={googleMapConfig.id}
                    mapContainerClassName={isMobile ? 'position-relative' : 'd-block m-auto'}
                    mapContainerStyle={{
                        height: isMobile ? '600px' : '700px',
                        width: '100%',
                        borderRadius: '12px',
                        border: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6'
                    }}
                    onLoad={mapInstance => {map.current = mapInstance}}
                    center={centerCoords}
                    zoom={17.8}
                    options={{
                        disableDefaultUI: false,
                        zoomControl: true,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true,
                        rotateControl: true,
                        gestureHandling: 'greedy'
                    }}
                >
                    {hthTerritories?.length && hthTerritories.map((territory, territoryIndex) => {
                        // Color según modo selección
                        let territoryColor = getTerritoryColor(territory.territoryNumber);
                        if (selectionMode) {
                          territoryColor = selectedTerritories.includes(territory.territoryNumber)
                            ? getTerritoryColor(territory.territoryNumber)
                            : '#bdbdbd';
                        }
                        // Agrupar polígonos por manzana (block)
                        const blocks = new Map<string, typeHTHFace[]>();
                        territory.faces.forEach(polygon => {
                            if (!blocks.has(polygon.block)) {
                                blocks.set(polygon.block, []);
                            }
                            blocks.get(polygon.block)!.push(polygon);
                        });
                        return Array.from(blocks.entries()).map(([block, blockPolygons], blockIndex) => {
                            const blockCoordinates = hthHelper.GetBlockCoordinates(blockPolygons, block);
                            const blockCenter = hthHelper.GetBlockCenter(blockCoordinates);
                            if (blockCoordinates.length < 3) {
                                return null;
                            }
                            let fillColor = territoryColor;
                            let strokeColor = getTerritoryColor(territory.territoryNumber);
                            if (selectionMode) {
                                fillColor = selectedTerritories.includes(territory.territoryNumber)
                                    ? territoryColor
                                    : '#bdbdbd';
                                strokeColor = getTerritoryColor(territory.territoryNumber);
                            }
                            return (
                              <React.Fragment key={`${territory.territoryNumber}-${block}`}>
                                    <BlockPolygon
                                        path={blockCoordinates}
                                        color={fillColor}
                                        strokeColor={strokeColor}
                                        isDarkMode={isDarkMode}
                                        onClick={() => {
                                            if (selectionMode) {
                                            handleSelectTerritory(territory.territoryNumber);
                                            } else {
                                            openModalHandler(territory.territoryNumber);
                                            }
                                        }}
                                        label={showNumbers && blockIndex === 0 ? territory.territoryNumber : ''}
                                        center={blockCenter}
                                    />
                                    {showDates && blockIndex === 0 && territoryDates[territory.territoryNumber] && (
                                        <Marker
                                            position={{ lat: blockCenter.lat + 0.00013, lng: blockCenter.lng }}
                                            options={{
                                                label: {
                                                    text: hthHelper.FormatDateForExcel(territoryDates[territory.territoryNumber]),
                                                    color: '#222',
                                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    className: 'territory-date-label',
                                                },
                                                icon: {
                                                    url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="60" height="22">
                                                            <rect x="0" y="0" width="60" height="22" rx="6" fill="white" stroke="#bbb" stroke-width="1.5"/>
                                                        </svg>
                                                    `),
                                                    anchor: new window.google.maps.Point(30, 22),
                                                    scaledSize: new window.google.maps.Size(60, 22),
                                                },
                                                zIndex: 1000,
                                            }}
                                        />
                                    )}
                              </React.Fragment>
                            );
                        });
                    })}

                    {/* Marcadores de familias activas */}
                    {showFamilies && familias.filter(f => f.activo && f.lat && f.lng).map(f => (
                        <Marker
                            key={f.id}
                            position={{ lat: f.lat!, lng: f.lng! }}
                            options={{
                                label: {
                                    text: extractInitials(f.familia),
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                },
                                icon: {
                                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="red" stroke="white" stroke-width="1"/>
                                        <polyline points="9,22 9,12 15,12 15,22" fill="red"/>
                                    </svg>
                                    `),
                                    scaledSize: new google.maps.Size(36, 36),
                                    anchor: new google.maps.Point(16, 12)
                                },
                            }}
                            onClick={() => setSelectedFamilia(f)}
                        />
                    ))}
                    {selectedFamilia && (
                        <InfoWindow
                            position={{ lat: selectedFamilia.lat!, lng: selectedFamilia.lng! }}
                            onCloseClick={() => setSelectedFamilia(null)}
                        >
                            <div>
                                <strong>{selectedFamilia.familia}</strong><br />
                                {selectedFamilia.direccion}<br />
                                {selectedFamilia.diasHorarios}
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </div>

             {/* Botón de importar archivo Excel y mostrar/ocultar fechas, mejor organizados */}
            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                <label className="btn btn-outline-secondary btn-sm mb-0">
                    Importar S-13 en Excel
                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        onChange={e => {
                            if (e.target.files && e.target.files[0]) {
                                handleFile(e.target.files[0]);
                            }
                        }}
                    />
                </label>
                {loadingDates && (
                    <span className="text-info small ms-2">Procesando archivo...</span>
                )}
                {errorDates && (
                    <span className="text-danger small ms-2">{errorDates}</span>
                )}
            </div>

            {/* Tabla de familias */}
            <div className="mt-5">
                <h3 className="text-center mb-3">Familias en el mapa</h3>
                <button
                    className="btn btn-success mb-4"
                    onClick={() => {
                        if (adding) {
                            resetForm();
                        } else {
                            setAdding(true);
                            setEditId(null);
                        }
                    }}
                    disabled={loadingFamilia}
                >
                    {adding ? 'Cerrar formulario' : 'Agregar familia'}
                </button>
              {adding && (
                <form className={`mb-3 card ${isDarkMode ? 'bg-lightdark border-light' : 'bg-light border'} p-3 border-1 rounded-6 shadow-sm`} onSubmit={handleAddFamilia}>
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <input ref={familiaInputRef} required className="form-control" placeholder="Familia" value={form.familia} onChange={e => setForm(f => ({ ...f, familia: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <Autocomplete
                        onLoad={ac => setAutocomplete(ac)}
                        onPlaceChanged={() => {
                          if (autocomplete) {
                            const place = autocomplete.getPlace();
                            const direccion = place.formatted_address || place.name || '';
                            setForm(f => ({ ...f, direccion }));
                          }
                        }}
                      >
                        <input
                          required
                          className="form-control"
                          placeholder="Dirección"
                          value={form.direccion}
                          onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))}
                          type="text"
                        />
                      </Autocomplete>
                    </div>
                    <div className="col-md-3">
                      <input required className="form-control" placeholder="Días y horarios" value={form.diasHorarios} onChange={e => setForm(f => ({ ...f, diasHorarios: e.target.value }))} />
                    </div>
                    <div className="col-md-3 d-flex justify-content-end gap-2">
                      <button className="btn btn-general-blue" type="submit" disabled={geocodingLoading || loadingFamilia}>
                        {loadingFamilia ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {editId ? 'Modificando...' : 'Agregando...'}
                          </>
                        ) : (
                          editId ? 'Modificar' : 'Agregar'
                        )}
                      </button>
                      <button className="btn btn-general-red" type="button" onClick={() => {
                        resetForm();
                      }} disabled={loadingFamilia}>Cancelar</button>
                    </div>
                  </div>
                </form>
              )}
              <div className="table-responsive">
                <table className={`table table-bordered ${isDarkMode ? 'table-dark' : ''} table-sm align-middle`}>
                  <thead>
                    <tr>
                      <th>Familia</th>
                      <th>Dirección</th>
                      <th>Descripción y disponibilidad</th>
                      <th>Activo</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {familias.map(f => (
                      <tr key={f.id}>
                        <td>{f.familia}</td>
                        <td>{f.direccion}</td>
                        <td>{f.diasHorarios}</td>
                        <td className={'text-center'}>
                          {/* <input 
                            type="checkbox" 
                            checked={f.activo} 
                            onChange={() => handleToggleActivo(f.id)} 
                            disabled={loadingFamilia}
                          /> */}
                          <input type={'checkbox'}
                              className={'form-check-input checkbox-blue checkbox-md me-2'}
                              checked={f.activo}
                              onChange={() => handleToggleActivo(f.id)} 
                              disabled={loadingFamilia}
                          />
                          {loadingFamilia && (
                            <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-general-blue btn-sm me-1" onClick={() => {
                            setForm({
                              familia: f.familia,
                              direccion: f.direccion,
                              diasHorarios: f.diasHorarios,
                              activo: f.activo
                            });
                            setEditId(f.id);
                            setAdding(true);
                          }} disabled={loadingFamilia}>Modificar</button>
                          <button className="btn btn-general-red btn-sm" onClick={() => handleDeleteFamilia(f.id)} disabled={loadingFamilia}>
                            {loadingFamilia ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                Eliminando...
                              </>
                            ) : (
                              'Baja'
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
        </div>
    );
}
